// useObsActive.js
// Returns whether this OBS browser source is currently ON-AIR (in the active /
// program scene). OBS pushes the `obsSourceActiveChanged` CustomEvent to the
// page synchronously the instant a scene goes on/off air — no polling, no
// obs-websocket, no round-trip. We use it to PAUSE expensive animations on
// off-air overlays (the 6 scenes you're not showing keep burning GPU
// otherwise), which is the single biggest lever for heat that accumulates over
// a multi-hour event.
//
// IMPORTANT design choices:
//  - The source is NOT shut down — only its animations freeze. The DOM stays
//    mounted, data stays loaded, fonts stay swapped, layout stays settled.
//    Resuming is free: it un-freezes already-rendered compositor layers, so
//    there is no cold-start hitch when a scene comes back on-air.
//  - Default is TRUE (animate). Outside OBS (normal browser, local dev) the
//    event never fires, so animations always run. Inside OBS, a source that
//    loads before its first scene-switch also animates until told otherwise.
//    This is the fail-safe direction: worst case is a little wasted animation
//    on an off-air scene, never a frozen-looking visible scene.
//  - Gates on `active` (program / on-air), which is correct for a non-Studio-
//    Mode setup (this booth does not use Studio Mode).
import { useEffect, useState } from "react";

export default function useObsActive() {
  const [active, setActive] = useState(true);

  useEffect(() => {
    // Not inside an OBS browser source → stay default-on.
    if (typeof window === "undefined" || !window.obsstudio) return;
    const onActive = (e) => setActive(!!(e && e.detail && e.detail.active));
    window.addEventListener("obsSourceActiveChanged", onActive);
    return () => window.removeEventListener("obsSourceActiveChanged", onActive);
  }, []);

  return active;
}
